import React, { useCallback, useState } from 'react'
import { translate } from '../../../../selectors/translations'
import { formatDate } from '../../../../utils/datetime'
import {
  ProgramConfigChangedField,
  ProgramConfigInteractionHistoryDto,
  ProgramTaskActionType
} from "../../../../sdk"
import { generateInteractor } from "../../util"
import useTranslate from "../../../../hooks/useTranslate"
import PatientSideHistoryLayout from "../PatientSideHistoryLayout"

type PropsFromState = {
  history: ProgramConfigInteractionHistoryDto[];
}

const ProgramConfigChangeHistory: React.FC<PropsFromState> = (props: PropsFromState) => {
  const {history} = props
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const handleExpandClick = useCallback((): void => {
    setIsExpanded((prevState) => !prevState)
  }, [])
  const translateLocalazy = useTranslate()
  const interactor = generateInteractor()

  return (<PatientSideHistoryLayout
      isExpanded={isExpanded}
      handleExpandClick={handleExpandClick}
      title={translateLocalazy('patient.history.program-config')}
    >
      {isExpanded && <table className="table--colored">
          <thead>
          <tr>
              <th>{translate('change')}</th>
              <th>{translate('transcription from')}</th>
              <th>{translate('transcription to')}</th>
              <th>{translate('date')}</th>
              <th>{translate('editor')}</th>
          </tr>
          </thead>
          <tbody>
          {
            history.length
              ? history.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))
                .map((change, index) => {
                  const inter = change.metadata.automaticDecision ? 'system' : (change.metadata.patientDecision ? 'patient' : interactor(change.interactor))
                  const isLvlChange = change.metadata.changedField === ProgramConfigChangedField.CURRENT_LVL
                  const isBool = [ProgramConfigChangedField.ENABLED, ProgramConfigChangedField.LOCKED_BY_DOCTOR].includes(change.metadata.changedField)
                  const oldValue = isBool ? (change.metadata.oldValue ? 'on' : 'off') : change.metadata.oldValue
                  const newValue = isBool ? (change.metadata.newValue ? 'on' : 'off') : change.metadata.newValue

                  return (
                    <tr key={index}>
                      <td>{translateLocalazy(changedActionName(change.metadata.action))} - {translateLocalazy(changedFieldName(change.metadata.changedField))}</td>
                      <td>{isLvlChange ? change.metadata.oldValue + 1 : oldValue}</td>
                      <td>{isLvlChange ? change.metadata.newValue + 1 : newValue}</td>
                      <td>{formatDate(new Date(change.createdAt))}</td>
                      <td>{inter}</td>
                    </tr>
                  )
                })
              : (<tr>
                <td className="t_center" colSpan={5}>{translate('no results')}</td>
              </tr>)
          }
          </tbody>
      </table>}
    </PatientSideHistoryLayout>
  )
}

function changedActionName(action: ProgramTaskActionType) {
  switch (action) {
    case ProgramTaskActionType.GAMES:
      return 'patient.administrate.configure-tasks.games'
    case ProgramTaskActionType.SLEEP:
      return 'patient.administrate.configure-tasks.sleep'
    case ProgramTaskActionType.STEPS:
      return 'patient.administrate.configure-tasks.steps'
    case ProgramTaskActionType.SOCIAL_INTERACTION:
      return 'patient.administrate.configure-tasks.social-interaction'
    case ProgramTaskActionType.STRESS:
      return 'patient.administrate.configure-tasks.stress'
    case ProgramTaskActionType.DIET:
      return 'patient.administrate.configure-tasks.diet'
  }

}

function changedFieldName(field: ProgramConfigChangedField) {
  switch (field) {
    case ProgramConfigChangedField.START_TARGET:
      return 'patient.administrate.configure-tasks.start-target'
    case ProgramConfigChangedField.INCREASE_AMOUNT_PER_LVL:
      return 'patient.administrate.configure-tasks.increase-per-lvl'
    case ProgramConfigChangedField.INCREASE_LVL_AFTER_DAYS:
      return 'patient.administrate.configure-tasks.days-streak'
    case ProgramConfigChangedField.MAX_TARGET:
      return 'patient.administrate.configure-tasks.max-target'
    case ProgramConfigChangedField.CURRENT_LVL:
      return 'patient.administrate.configure-tasks.lvl'
    case ProgramConfigChangedField.ENABLED:
      return 'patient.administrate.configure-tasks.enabled'
    case ProgramConfigChangedField.LOCKED_BY_DOCTOR:
      return 'patient.administrate.configure-tasks.locked'
  }
}

export default ProgramConfigChangeHistory
